import { useState, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { BugReport as BugReportIcon } from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
}

export default function BugReportModal({ open, onClose, userEmail }: BugReportModalProps) {
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const functions = getFunctions();

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Please provide a description of the bug');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Collect system information
      const systemInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };

      // Call the cloud function
      const submitBugReport = httpsCallable(functions, 'submitBugReport');
      await submitBugReport({
        description,
        steps,
        systemInfo,
        userEmail,
      });
      
      setSuccess(true);
      setDescription('');
      setSteps('');
      setTimeout(onClose, 2000);
    } catch (err: any) {
      console.error('Error submitting bug report:', err);
      setError(err.message || 'Failed to submit bug report. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const handleStepsChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setSteps(e.target.value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugReportIcon color="error" />
        Report a Bug
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {success ? (
            <Alert severity="success">
              Thank you for your bug report! We'll investigate the issue.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please provide as much detail as possible about the bug you encountered.
                This will help us fix the issue more quickly.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Bug Description"
                placeholder="Describe what happened and what you expected to happen..."
                value={description}
                onChange={handleDescriptionChange}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Steps to Reproduce"
                placeholder="1. First step&#10;2. Second step&#10;3. Third step"
                value={steps}
                onChange={handleStepsChange}
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <BugReportIcon />}
        >
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 